import { LuIcon, Menu, type MenuRootProps, type MenuVirtualTriggerProps } from "@overmap-ai/blocks"
import { type Asset, OrganizationAccessLevel } from "@overmap-ai/common"
import { selectAssetById, selectAssetTypeById, selectAssetTypeStatusesOfAssetType } from "@overmap-ai/core"
import { useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { useGeometryPicker } from "../../contexts/hooks"
import { useAsset, useDeleteAsset } from "../../hooks/assets"
import { useViewGeometryOnMap } from "../../mapbox/hooks"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { GeometryGroup } from "../MenuGroups/GeometryGroup"
import { AssetTypeStatusSelectGroup } from "../MenuSelectGroups/AssetTypeStatusSelectGroup"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"

enum AssetMarkerContextMenuPages {
	INITIAL = "initial",
	GEOMETRY = "geometry",
	STATUS = "status",
}

interface AssetMarkerContextMenuProps extends Omit<MenuRootProps, "children">, MenuVirtualTriggerProps {
	assetId: Asset["uuid"]
	onDelete?: () => void
}

export const AssetMarkerContextMenu = memo((props: AssetMarkerContextMenuProps) => {
	const { assetId, disabled, onDelete, virtualElement, ...rest } = props

	const asset = useAppSelector(selectAssetById(assetId))

	if (!asset) {
		throw new Error(`Expected asset to be defined, got ${asset}`)
	}

	const assetType = useAppSelector(selectAssetTypeById(asset.asset_type))

	if (!assetType) {
		throw new Error(`Expected asset to have a valid asset type, got ${assetType}`)
	}

	const { assetTypeStatus, handleStatusChange } = useAsset(asset)
	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))
	const deleteAsset = useDeleteAsset()
	const openGeometryPicker = useGeometryPicker()
	const navigate = useNavigate()
	const viewGeometryOnMap = useViewGeometryOnMap()

	const handleView = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/assets/$assetId",
			params: (prev) => {
				return {
					...prev,
					assetId: asset.uuid,
				}
			},
		})
	}, [asset.uuid, navigate])

	const handleGeometryChange = useCallback(() => {
		viewGeometryOnMap(asset.geometry)

		openGeometryPicker((close) => ({
			initialGeometry: asset.geometry,
			onSave: (geometry) => {
				close()

				void sdk.assets.update({
					uuid: asset.uuid,
					geometry: geometry,
				})
			},
			onCancel: close,
		}))
	}, [asset.geometry, asset.uuid, openGeometryPicker, viewGeometryOnMap])

	const handleDeleteAsset = useCallback(() => {
		deleteAsset(asset.uuid, onDelete)
	}, [asset.uuid, deleteAsset, onDelete])

	return (
		<Menu.Root {...rest}>
			<Menu.VirtualTrigger virtualElement={virtualElement} disabled={disabled} />
			<Menu.Content size="sm">
				<Menu.Label>
					<AssetTypeIcon assetTypeId={asset.asset_type} inline />
					<AssetIndexLabel className="truncate" highContrast assetId={assetId} />
				</Menu.Label>
				<Menu.Separator />
				<Menu.Pages defaultPage={AssetMarkerContextMenuPages.INITIAL}>
					<Menu.PageContent page={AssetMarkerContextMenuPages.INITIAL}>
						<Menu.Item onSelect={handleView}>
							<LuIcon icon={modelIconMapping.expand} />
							View
						</Menu.Item>
						<Menu.PageTrigger page={AssetMarkerContextMenuPages.GEOMETRY}>
							<LuIcon icon={modelIconMapping.geometry} />
							Geometry
						</Menu.PageTrigger>
						<Menu.PageTrigger page={AssetMarkerContextMenuPages.STATUS}>
							<LuIcon icon={modelIconMapping.status} />
							Status
						</Menu.PageTrigger>
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
							<Menu.Separator />
							<Menu.Item onSelect={handleDeleteAsset}>
								<LuIcon icon={modelIconMapping.delete} />
								Delete
							</Menu.Item>
						</OrganizationAccessRequired>
					</Menu.PageContent>
					<Menu.PageContent page={AssetMarkerContextMenuPages.GEOMETRY}>
						<GeometryGroup geometry={asset.geometry} onGeometryChange={handleGeometryChange} />
					</Menu.PageContent>
					<Menu.PageContent page={AssetMarkerContextMenuPages.STATUS}>
						<AssetTypeStatusSelectGroup
							assetTypeStatuses={assetTypeStatuses}
							required
							value={assetTypeStatus}
							onValueChange={handleStatusChange}
						/>
					</Menu.PageContent>
				</Menu.Pages>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetMarkerContextMenu.displayName = "AssetMarkerContextMenu"
